import React from 'react';
import { eur, cfa, taux, pourcent, decoupe } from '../format.js';
import Info from './Info.jsx';

/**
 * Carte résultat - document 03 section 6, document 05 « éléments à soigner ».
 *
 * Le chiffre principal doit être immédiatement identifiable ; le tableau
 * détaillé est secondaire, lisible mais discret. C'est la hiérarchie qui porte
 * la lecture, pas la couleur.
 */
export default function CarteResultat({ r, devise, basis }) {
  const sortant = r.direction === 'out';

  // Ce que l'utilisateur cherche dépend de ce qu'il a fixé : s'il fixe l'envoi,
  // il veut savoir ce qui arrive ; s'il fixe la réception, ce qu'il doit
  // envoyer.
  const principal = sortant
    ? (basis === 'envoi'
        ? { libelle: 'Le destinataire reçoit', valeur: cfa(r.netCfa, devise) }
        : { libelle: 'Le client doit envoyer', valeur: eur(r.montantEnvoye) })
    : (basis === 'envoi'
        ? { libelle: 'Le bénéficiaire reçoit', valeur: eur(r.recuEur) }
        : { libelle: 'Le client doit remettre', valeur: cfa(r.montantCollecte, devise) });

  const lignes = sortant
    ? [
        ['Montant envoyé', eur(r.montantEnvoye)],
        ['Frais de service', eur(r.fraisEur)],
        ['Brut converti', cfa(r.brutCfa, devise)],
        r.opTotal > 0 && ['Frais opérateur', cfa(r.opTotal, devise)],
        ['Net crédité', cfa(r.netCfa, devise)],
        ['Provision à avoir', cfa(r.provision, devise)],
        // N'apparaît qu'en refacturé : dans les deux autres modes, le client ne
        // règle rien de plus que le montant envoyé.
        r.feeMode === 'refacture' && ['Total réglé par le client', eur(r.totalRegleEur)],
        ['Coût client', `${eur(r.coutClient)} (${pourcent(r.coutClientPct)})`],
      ]
    : [
        ['Montant collecté', cfa(r.montantCollecte, devise)],
        ['Frais de service', cfa(r.fraisCfa, devise)],
        ['Net converti', cfa(r.netCfa, devise)],
        ['Reçu par le bénéficiaire', eur(r.recuEur)],
        r.opTotal > 0 && ['Frais opérateur (à la charge du client)', cfa(r.opTotal, devise)],
      ];

  const detail = decoupe(r.opTranches, devise);

  return (
    <section className="carte" aria-label="Résultat de la cotation">
      <div className="etiquette">{principal.libelle}</div>
      {/* La taille suit la longueur du montant. Un chiffre fixe à 2 rem tient
          pour « 328 000 XAF » et déborde pour « 26 000 000 000 XAF » - or c'est
          le même écran, et c'est justement sur les grosses sommes qu'on veut
          lire sans effort. `clamp` borne la réduction : en dessous de 1,25 rem
          le montant principal cesserait de dominer la carte. */}
      <div
        className="chiffre"
        style={{
          fontSize: `clamp(1.25rem, ${Math.min(2, 26 / Math.max(principal.valeur.length, 8)).toFixed(2)}rem, 2rem)`,
          fontWeight: 700, margin: 'var(--espace-1) 0 var(--espace-2)',
        }}
      >
        {principal.valeur}
      </div>
      <div style={{ color: 'var(--couleur-encre-douce)', fontSize: '0.85rem' }}>
        Taux effectif <span className="chiffre">{taux(r.tauxEffectif)}</span> {devise}/EUR
        <Info titre="Taux effectif">
          Le taux réellement obtenu par le client une fois tous les frais
          inclus : ce qu’il reçoit divisé par ce qu’il paie. C’est le seul
          chiffre comparable à l’offre d’un concurrent, parce qu’il ne laisse
          rien de côté.
        </Info>
      </div>

      <dl style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--espace-1) var(--espace-3)',
        marginTop: 'var(--espace-4)', marginBottom: 0, fontSize: '0.875rem',
      }}>
        {lignes.filter(Boolean).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ color: 'var(--couleur-encre-douce)' }}>{k}</dt>
            <dd className="chiffre" style={{ margin: 0, textAlign: 'right' }}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>

      {detail && (
        <p style={{
          marginTop: 'var(--espace-3)', marginBottom: 0,
          fontSize: '0.8rem', color: 'var(--couleur-encre-douce)',
        }}>
          Découpe opérateur - <span className="chiffre">{detail}</span>
        </p>
      )}
    </section>
  );
}
